import axios, {
  AxiosHeaders,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearToken, getToken, setToken } from '@dts/shared/src/utils/system';
import webConfig from '@dts/shared/src/utils/httpUrlConfig';

const SUCCESS_STATUS = '200';
const LOGIN_EXPIRED_CODES = new Set(['401', '403']);
const NO_PERMISSION_STATUS = '412';

export interface ApiResponse<T> {
  status: string;
  msg: string;
  data: T;
}

export class ApiRequestError extends Error {
  status: string;
  response?: ApiResponse<unknown>;

  constructor(message: string, status = '500', response?: ApiResponse<unknown>) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.response = response;
  }
}

export type RequestOptions = Omit<AxiosRequestConfig, 'url' | 'method' | 'data' | 'params'>;

const serializeQueryParams = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();

  const appendParam = (key: string, value: unknown) => {
    if (value === undefined) {
      return;
    }
    if (value === null) {
      searchParams.append(key, '');
      return;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => appendParam(key, item));
      return;
    }
    if (value instanceof Date) {
      searchParams.append(key, value.toISOString());
      return;
    }
    if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
        appendParam(`${key}[${childKey}]`, childValue);
      });
      return;
    }
    searchParams.append(key, String(value));
  };

  Object.entries(params).forEach(([key, value]) => {
    appendParam(key, value);
  });

  return searchParams.toString();
};

const httpClient = axios.create({
  baseURL: webConfig.apiUrl,
  timeout: 60000,
});

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getToken();
  if (token) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set('X-Token', token);
    config.headers = headers;
  }
  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    const newToken = response.headers?.newtoken ?? response.headers?.newToken;
    if (newToken) {
      setToken(String(newToken));
    }
    return response;
  },
  (error) => {
    if (axios.isCancel(error)) {
      return new Promise(() => {});
    }

    const body = error.response?.data as ApiResponse<unknown> | undefined;
    const status = body?.status ?? String(error.response?.status ?? '500');
    const message = body?.msg ?? error.message ?? 'Request failed';
    throw new ApiRequestError(message, status, body);
  },
);

export const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const response = await httpClient.request<ApiResponse<T>>({
    ...(config.params && !config.paramsSerializer
      ? {
          paramsSerializer: {
            serialize: (params) => serializeQueryParams(params as Record<string, unknown>),
          },
        }
      : {}),
    ...config,
  });
  const body = response.data;

  if (String(response.status) !== SUCCESS_STATUS) {
    throw new ApiRequestError('Request failed', String(response.status), body);
  }

  if (!body || body.status === SUCCESS_STATUS) {
    return body?.data as T;
  }

  if (LOGIN_EXPIRED_CODES.has(body.status)) {
    clearToken();
  }

  if (body.status === NO_PERMISSION_STATUS && window.location.href.includes('/ht')) {
    window.location.href = `${webConfig.online}/ht/#/NoPermission`;
  }

  throw new ApiRequestError(body.msg || 'Request failed', body.status, body);
};

export const get = <T, P extends object = Record<string, unknown>>(
  url: string,
  params?: P,
  config: RequestOptions = {},
) => {
  return request<T>({
    ...config,
    url,
    method: 'get',
    params,
  });
};

export const post = <T, B = unknown>(url: string, data?: B, config: RequestOptions = {}) => {
  return request<T>({
    ...config,
    url,
    method: 'post',
    data,
  });
};

export default httpClient;
